import { createPortal } from "react-dom";
import { useEffect, useRef } from "react";
import { computePosition, flip, shift, offset } from '@floating-ui/dom';

/**
 * DropdownMenu Component with Portal and Floating UI
 * Renders dropdown at document.body level with proper positioning
 * 
 * Props:
 * - anchorRect: DOMRect from getBoundingClientRect()
 * - onClose: function to call when clicking outside
 * - children: dropdown content
 */
export default function DropdownMenu({ anchorRect, onClose, children }) {
  const ref = useRef(null);

  // Position the dropdown using floating-ui
  useEffect(() => {
    if (!anchorRect || !ref.current) return;

    // Create a virtual element from anchorRect
    const virtualElement = {
      getBoundingClientRect: () => anchorRect,
    };

    computePosition(virtualElement, ref.current, {
      placement: 'bottom-start',
      middleware: [
        offset(6),
        flip(),
        shift({ padding: 8 })
      ],
    }).then(({ x, y }) => {
      Object.assign(ref.current.style, {
        left: `${x}px`,
        top: `${y}px`,
      });
    });
  }, [anchorRect]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  // Close on Escape key
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        onClose();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  if (!anchorRect) return null;

  return createPortal(
    <div
      ref={ref}
      style={{
        position: "fixed",
        minWidth: 200,
        maxWidth: 320,
        zIndex: 1000,
      }}
      className="
        bg-white
        border border-black/10
        rounded-lg
        shadow-2xl
        py-1
        text-sm
      "
    >
      {children}
    </div>,
    document.body
  );
}
